import React, { useState, useRef, useEffect } from "react";
import {
  Input,
  Label,
  RadioButton,
  FlexBox,
  FlexBoxDirection,
  Button,
  Dialog,
  CheckBox,
  ComboBox, 
  ComboBoxItem 
} from "@ui5/webcomponents-react";
import UIModel from "../models/UIModel.json";
import localforage from 'localforage';


const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const ManualForm = ({ formData, onFormChange, handleNext, setFormData = () => {} }) => {
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [latitude, setLatitude] = useState(formData.latitude || "");
  const [longitude, setLongitude] = useState(formData.longitude || "");
  const [addressSearch, setAddressSearch] = useState("");
  const [provinces, setProvinces] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [prevProvinces, setPrevProvinces] = useState([]);
  const [prevMunicipalities, setPrevMunicipalities] = useState([]);
  const [selectedPrevProvince, setSelectedPrevProvince] = useState(null);
  const [barangays, setBarangays] = useState([]);
  const [isMapScriptLoaded, setIsMapScriptLoaded] = useState(!!window.google?.maps); // Track script load status
  const [locationData, setLocationData] = useState(null);
  const [autoFilled, setAutoFilled] = useState(false);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const autocompleteRef = useRef(null);
  const googleMap = useRef(null);
  const barangayCode = localStorage.getItem("barangayCode");
  const municipalCode = localStorage.getItem("municipalCode");
  const provincialCode = localStorage.getItem("provincialCode")

  // Load Google Maps script dynamically
  useEffect(() => {
    if (!window.google?.maps) { // Check specifically for maps object
      if (!document.querySelector('script[src*="maps.googleapis.com"]')) { // Prevent adding multiple scripts
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
        script.async = true;
        script.onload = () => {
          console.log("Google Maps with Places Loaded");
          setIsMapScriptLoaded(true); // Set state when loaded
        };
        script.onerror = () => {
          console.error("Failed to load Google Maps script.");
          setMapLoading(false); // Stop loading indicator on error
        };
        document.body.appendChild(script);
      }
    } else {
      setIsMapScriptLoaded(true); // Already loaded
    }
  }, []);

  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
    };
  }, []);

  // Fetch provinces
  const fetchProvinces = async () => {
    const cacheProvinceKey = 'location_province';
    try {
      const data = await localforage.getItem(cacheProvinceKey);
      setProvinces(data.data);
    } catch (error) {
      console.error("Failed to fetch provinces:", error);
    }
  };

  const fetchPrevProvinces = async () => {
    const cacheProvinceKey = 'location_province';
    try {
      const data = await localforage.getItem(cacheProvinceKey);
      setPrevProvinces(data.data);
    } catch (error) {
      console.error("Failed to fetch provinces:", error);
    }
  };

  const fetchPrevMunicipalities = async (provinceId) => {
    const cacheMunicipalityKey = 'location_municipality';
    try {
      const data = await localforage.getItem(cacheMunicipalityKey);
      const filteredData = Array.isArray(data.data)
      ? data.data.find(item => item.province_id === Number(provinceId))?.municipalities
      : [];
      setPrevMunicipalities(filteredData);
    } catch (error) {
      console.error("Failed to fetch municipalities:", error);
    }
  };

  // Fetch municipalities based on province
  const fetchMunicipalities = async (provinceId) => {
    const cacheMunicipalityKey = 'location_municipality';
    try {
      const data = await localforage.getItem(cacheMunicipalityKey);
      const filteredData = Array.isArray(data.data)
      ? data.data.find(item => item.province_id === Number(provinceId))?.municipalities
      : [];
      setMunicipalities(filteredData);
    } catch (error) {
      console.error("Failed to fetch municipalities:", error);
    }
  };

  // Fetch barangays based on municipality
  const fetchBarangays = async (municipalityId) => {
    const cacheBarangayKey = 'location_barangay';
    try {
      const data = await localforage.getItem(cacheBarangayKey);
      const filteredData = Array.isArray(data.data)
      ? data.data.find(item => item.municipality_id === Number(municipalityId))?.barangays
      : [];
      setBarangays(filteredData);
    } catch (error) {
      console.error("Failed to fetch barangays:", error);
    }
  };

  useEffect(() => {
    const fetchLocations = async () => {
      const [barangays, municipalities, provinces] = await Promise.all([
        localforage.getItem('location_barangay'),
        localforage.getItem('location_municipality'),
        localforage.getItem('location_province'),
      ]);
      setLocationData({ barangays, municipalities, provinces });
    };

    fetchLocations();
  }, []);



  useEffect(() => {
    if (!barangayCode && formData.barangay) return;
    const selectedBarangayCode = barangayCode || formData.barangay;
    if (!locationData?.barangays?.data) return;
    if (autoFilled) return; // prevent re-triggering

    const barangayContainer = locationData.barangays.data.find(b =>
      b.barangays.some(brgy => brgy.barangay_id === parseInt(selectedBarangayCode))
    );

    if (!barangayContainer) return;

    const barangay = barangayContainer.barangays.find(b => b.barangay_id === selectedBarangayCode);
    const municipality_id = barangayContainer.municipality_id;
    const municipalityContainer = locationData.municipalities.data.find(m =>
      m.municipalities.some(mu => mu.municipality_id === municipality_id)
    );

    if (!municipalityContainer) return;

    const municipality = municipalityContainer.municipalities.find(m => m.municipality_id === municipality_id);
    const province_id = municipalityContainer.province_id;
    const province = locationData.provinces.data.find(p => p.province_id === province_id);

    // update in order
    setProvinces(locationData.provinces.data);
    setMunicipalities(municipalityContainer.municipalities);
    setBarangays(barangayContainer.barangays);

    // set form data
    onFormChange("province", String(province_id));
    onFormChange("cityMunicipality", String(municipality_id));
    onFormChange("barangay", String(selectedBarangayCode));

    setAutoFilled(true);

  }, [locationData]);

  useEffect(() => {
    // if (autoFilled) return;

    fetchProvinces();

    // If a province is already selected, fetch its municipalities
    if (formData.province) {
      fetchMunicipalities(formData.province);
    }

    // If a municipality is already selected, fetch its barangays
    if (formData.cityMunicipality) {
      fetchBarangays(formData.cityMunicipality);
    }
  }, [formData.province, formData.cityMunicipality, autoFilled]);

  useEffect(() => {
    fetchPrevProvinces();

    if (formData.prevLocation && locationData && locationData.provinces && locationData.municipalities) {
      const prevMunicipalityId = parseInt(formData.prevLocation);
      const municipalities = locationData.municipalities.data;

      const municipalityContainer = municipalities.find(m =>
        m.municipalities.some(mu => mu.municipality_id === prevMunicipalityId)
      );

      if (!municipalityContainer) return;

      const municipality = municipalityContainer.municipalities.find(m => m.municipality_id === prevMunicipalityId);
      const province_id = municipalityContainer.province_id;
      
      setPrevMunicipalities(municipalityContainer.municipalities);
      setSelectedPrevProvince(province_id);

      setPrevProvinces(locationData.provinces.data);
    }
  }, [locationData]);

  // Effect to initialize map when dialog opens AND script is loaded
  useEffect(() => {
    if (mapDialogOpen && isMapScriptLoaded && mapRef.current) {
      setMapLoading(true); // Show loading just before init
      // Use a small timeout to allow the dialog to render fully before map init
      const timer = setTimeout(() => {
        initMap();
        setMapLoading(false); // Hide loading after init attempt
      }, 100); // Adjust delay if needed, ensures DOM element is ready
      return () => clearTimeout(timer); // Cleanup timer
    } else if (mapDialogOpen && !isMapScriptLoaded) {
      setMapLoading(true); // Keep loading indicator if script isn't ready yet
    }
  }, [mapDialogOpen, isMapScriptLoaded]); // Rerun when dialog opens or script loads

  // Open Google Maps modal
  const openGoogleMaps = () => {
    setMapDialogOpen(true);
    // Set loading state immediately if script isn't loaded yet
    if (!isMapScriptLoaded) {
      setMapLoading(true);
    }
    // initMap will be called by the useEffect hook when ready
  };

  // Initialize Google Map
  const initMap = () => {
    // Guard clause: Ensure Google Maps API is loaded and mapRef is available
    if (!window.google || !window.google.maps || !mapRef.current) {
      console.error("Google Maps API not loaded or map container not ready.");
      setMapLoading(false); // Ensure loading is hidden if init fails early
      return;
    }

    // Default coordinates (Mall of Asia as fallback)
    const defaultPosition = { lat: 14.5354, lng: 120.9821 };
    const mapOptions = {
      zoom: 14,
      center: defaultPosition
    };
  
    const map = new window.google.maps.Map(mapRef.current, mapOptions);
    googleMap.current = map;
  
    // Try to get user's current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userPosition = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          
          // Center map on user's location
          map.setCenter(userPosition);
          placeMarker(userPosition, map);
        },
        (error) => {
          console.warn("Geolocation error:", error);
          // Use default position if geolocation fails
          placeMarker(defaultPosition, map);
        }
      );
    } else {
      // Browser doesn't support Geolocation
      placeMarker(defaultPosition, map);
    }
  
    // Initialize search box
    const input = document.getElementById("autocomplete-input");
    const searchBox = new window.google.maps.places.SearchBox(input);
  
    map.addListener("bounds_changed", () => {
      searchBox.setBounds(map.getBounds());
    });
  
    searchBox.addListener("places_changed", () => {
      const places = searchBox.getPlaces();
      if (places.length === 0) return;
  
      // Clear existing markers
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
  
      const bounds = new window.google.maps.LatLngBounds();
      places.forEach(place => {
        if (!place.geometry) return;
  
        // Create and place marker
        markerRef.current = new window.google.maps.Marker({
          map,
          position: place.geometry.location,
          title: place.name
        });
  
        if (place.geometry.viewport) {
          bounds.union(place.geometry.viewport);
        } else {
          bounds.extend(place.geometry.location);
        }
      });
      
      map.fitBounds(bounds);
      updateFormWithPlace(places[0]);
    });
  
    // Click listener for manual marker placement
    map.addListener("click", (event) => {
      placeMarker(event.latLng, map);
    });
  };

  const updateFormWithPlace = (place) => {
    if (!place.geometry) return;
  
    const addressComponents = place.address_components || [];
    const address = {
      street: "",
      houseNo: "",
      purokSitio: "",
      barangay: "",
      cityMunicipality: "",
      province: "",
    };
  
    addressComponents.forEach(component => {
      if (component.types.includes("street_number")) {
        address.houseNo = component.long_name;
      } else if (component.types.includes("route")) {
        address.street = component.long_name;
      } else if (component.types.includes("sublocality")) {
        address.purokSitio = component.long_name;
      } else if (component.types.includes("administrative_area_level_2")) {
        address.barangay = component.long_name;
      } else if (component.types.includes("administrative_area_level_1")) {
        address.province = component.long_name;
      } else if (component.types.includes("locality") || component.types.includes("neighborhood")) {
        address.cityMunicipality = component.long_name;
      }
    });
  
    setFormData(prev => ({
      ...prev,
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
      houseNo: prev.houseNo || address.houseNo,
      street: prev.street || address.street,
      purokSitio: prev.purokSitio || address.purokSitio,
      barangay: prev.barangay,
      cityMunicipality: prev.cityMunicipality,
      province: prev.province,
      
    }));
  };


  // Place marker and fetch address
  const placeMarker = (location, map) => {
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    const marker = new window.google.maps.Marker({
      position: location,
      map: map,
    });

    markerRef.current = marker;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: location }, (results, status) => {
      if (status === "OK" && results[0]) {
        const position = marker.getPosition();
        const addressComponents = results[0].address_components;
        const address = {
          street: "",
          houseNo: "",
          purokSitio: "",
          barangay: "",
          cityMunicipality: "",
          province: "",
        };

        addressComponents.forEach(component => {
          if (component.types.includes("street_number")) {
            address.houseNo = component.long_name;
          } else if (component.types.includes("route")) {
            address.street = component.long_name;
          } else if (component.types.includes("sublocality")) {
            address.purokSitio = component.long_name;
          } else if (component.types.includes("administrative_area_level_2")) {
            address.cityMunicipality = component.long_name;
          } else if (component.types.includes("administrative_area_level_1")) {
            address.province = component.long_name;
          } else if (component.types.includes("locality")) {
            address.barangay = component.long_name;
          }
        });

        setFormData((prev) => ({
          ...prev,
          latitude: position.lat(),
          longitude: position.lng(),
          houseNo: address.houseNo,
          street: address.street,
          purokSitio: address.purokSitio,
        }));
      } else {
        console.error("Geocoder failed due to: " + status);
      }
    });
  };


  // Close Google Maps modal
  const closeMapModal = () => setMapDialogOpen(false);

  return (
    <div style={{ padding: "2rem", maxWidth: "900px", margin: "auto" }}>
      <h2>Step 1: General Information</h2>

      {/* Household Type */}
      <Label style={{ fontWeight: "bold" }}>A1. Household Type</Label>
      <span style={{ color: "red", marginLeft: "4px" }}>*</span>
      <FlexBox direction={FlexBoxDirection.Row} style={{ gap: "1rem", marginBottom: "1rem" }}>
        <RadioButton
          name="householdType"
          value="Family"
          checked={formData.householdType === "Family"}
          onChange={() => onFormChange("householdType", "Family")}
          text="Family"
        />
        <RadioButton
          name="householdType"
          value="Institutional Household"
          checked={formData.householdType === "Institutional Household"}
          onChange={() => onFormChange("householdType", "Institutional Household")}
          text="Institutional Household"
        />
      </FlexBox>

      
      <div style={{ marginBottom: "1.5rem" }}>
        <Label style={{ fontWeight: "bold" }}>A2. Address</Label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          {/* Street */}
          <div>
            <Label style={{ fontWeight: "bold" }}>Street/Subd.</Label>
            <Input
              name="street"
              value={formData.street || ""}
              onInput={(e) => onFormChange("street", e.target.value)}
              placeholder="Street/Subd."
              style={{ width: "100%", fontSize: "0.9rem" }}
              required
            />
          </div>

          {/* House No. */}
          <div>
            <Label style={{ fontWeight: "bold" }}>House No.</Label>
            <Input
              name="houseNo"
              value={formData.houseNo || ""}
              onInput={(e) => onFormChange("houseNo", e.target.value)}
              placeholder="House No."
              style={{ width: "100%", fontSize: "0.9rem" }}
              required
            />
          </div>

          {/* Purok/Sitio */}
          <div>
            <Label style={{ fontWeight: "bold" }}>Purok/Sitio</Label>
            <Input
              name="purokSitio"
              value={formData.purokSitio || ""}
              onInput={(e) => onFormChange("purokSitio", e.target.value)}
              placeholder="Purok/Sitio"
              style={{ width: "100%", fontSize: "0.9rem" }}
              required
            />
          </div>

          {/* Province */}
          <div>
            <Label style={{ fontWeight: "bold" }}>Province</Label>
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
            <select
              value={formData.province || ""}
              onChange={(e) => {
                const provinceId = e.target.value;
                onFormChange("province", provinceId);
                onFormChange("cityMunicipality", "");
                onFormChange("barangay", "");
                setMunicipalities([]);
                setBarangays([]);
                if (provinceId) fetchMunicipalities(provinceId);
              }}
              style={{ width: "100%", padding: "0.5rem" }}
              required
            >
              <option value="">Select Province</option>
              {Array.isArray(provinces) && provinces.length > 0 && 
                provinces.map((province) => (
                  <option key={province.province_id} value={province.province_id}>
                    {province.province_name}
                  </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div style={{ marginBottom: "1.5rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          

          {/* Municipality */}
          <div>
            <Label style={{ fontWeight: "bold" }}>City/Municipality</Label>
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
            <select
              value={formData.cityMunicipality || ""}
              onChange={(e) => {
                const municipalityId = e.target.value;
                onFormChange("cityMunicipality", municipalityId);
                onFormChange("barangay", "");
                setBarangays([]);
                if (municipalityId) fetchBarangays(municipalityId);
              }}
              disabled={!formData.province}
              style={{ width: "100%", padding: "0.5rem" }}
              required
            >
              <option value="">Select Municipality</option>
              {municipalities.map((municipality) => (
                <option key={municipality.municipality_id} value={municipality.municipality_id}>
                  {municipality.municipality_name}
                </option>
              ))}
            </select>
          </div>

          {/* Barangay */}
          <div>
            <Label style={{ fontWeight: "bold" }}>Barangay</Label>
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
            <select
              value={formData.barangay || ""}
              onChange={(e) => onFormChange("barangay", e.target.value)}
              disabled={!formData.cityMunicipality}
              style={{ width: "100%", padding: "0.5rem" }}
              required
            >
              <option value="">Select Barangay</option>
              {barangays.map((barangay) => (
                <option key={barangay.barangay_id} value={barangay.barangay_id}>
                  {barangay.barangay_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {/* Address Section in Table Layout with Proper Alignment */}
      <Button design="Transparent" onClick={openGoogleMaps}>
          Google Pin
      </Button>
      <div style={{ marginBottom: "1.5rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div>
            <Label style={{ fontWeight: "bold" }}>A3. Latitude</Label>
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
            <Input
              name="latitude"
              value={formData.latitude || ""}
              onInput={(e) => onFormChange("latitude", e.target.value)}
              placeholder="Latitude"
              style={{ width: "100%" }}
              required
            />
          </div>
          <div>
            <Label style={{ fontWeight: "bold" }}>A4. Longitude</Label>
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
            <Input
              name="longitude"
              value={formData.longitude || ""}
              onInput={(e) => onFormChange("longitude", e.target.value)}
              placeholder="Longitude"
              style={{ width: "100%" }}
              required
            />
          </div>
        </div>
      </div>
      <div style={{ marginBottom: "1.5rem" }}>
        <Label style={{ fontWeight: "bold" }}>A5. Name of Family Head</Label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          {/* A5. Pangalan ng Puno ng Pamilya */}
          <div>
            <Label style={{ fontWeight: "bold", fontSize: "1rem" }}>First Name</Label>
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
            <Input
              name="firstName"
              value={formData.firstName || ""}
              onInput={(e) => onFormChange("firstName", e.target.value.toUpperCase())}
              placeholder="First Name"
              style={{ width: "100%", fontSize: "0.9rem" }}
              required
            />
          </div>
          <div>
            <Label style={{ fontWeight: "bold", fontSize: "1rem" }}>Middle Name</Label>
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
            <Input
              name="middleName"
              value={formData.middleName || ""}
              onInput={(e) => onFormChange("middleName", e.target.value.toUpperCase())}
              placeholder="Middle Name"
              style={{ width: "100%", fontSize: "0.9rem" }}
              required
            />
          </div>
          <div>
            <Label style={{ fontWeight: "bold", fontSize: "1rem" }}>Last Name</Label>
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
            <Input
              name="lastName"
              value={formData.lastName || ""}
              onInput={(e) => onFormChange("lastName", e.target.value.toUpperCase())}
              placeholder="Last Name"
              style={{ width: "100%", fontSize: "0.9rem" }}
              required
            />
          </div>
          <div>
            <Label style={{ fontWeight: "bold", fontSize: "1rem" }}>Suffix (e.g., Jr., Sr.)</Label>
            <Input
              name="suffix"
              value={formData.suffix || ""}
              onInput={(e) => onFormChange("suffix", e.target.value.toUpperCase())}
              placeholder="Suffix"
              style={{ width: "100%", fontSize: "0.9rem" }}
            />
          </div>

          {/* A6. Family Type */}
          <div>
            <Label style={{ fontWeight: "bold", fontSize: "1rem" }}>A6. Family Type</Label>
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
            <FlexBox direction={FlexBoxDirection.Row} style={{ gap: "1rem", fontSize: "0.9rem" }}>
              <CheckBox
                name="familyType"
                text="Main Family (Owner)"
                checked={formData.familyType === "Main Family (Owner)"}
                onChange={() => onFormChange("familyType", "Main Family (Owner)")}
              />
              <CheckBox
                name="familyType"
                text="Extended Family"
                checked={formData.familyType === "Extended Family"}
                onChange={() => onFormChange("familyType", "Extended Family")}
              />
            </FlexBox>
          </div>
          {formData.familyType === "Extended Family" && (
            <div>
              <Label style={{ fontWeight: "bold", fontSize: "1rem" }}>Head ID of Main Family</Label>
              <span style={{ color: "red", marginLeft: "4px" }}>*</span>
              <Input
                name="headID"
                value={formData.headID || ""}
                onInput={(e) => onFormChange("headID", e.target.value)}
                placeholder="Head ID"
                style={{ width: "100%", fontSize: "0.9rem" }}
                required
              />
            </div>
          )}

          {/* A7. Status */}
          <div>
            <Label style={{ fontWeight: "bold", fontSize: "1rem" }}>A7. Status</Label>
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
            <FlexBox direction={FlexBoxDirection.Row} style={{ gap: "1rem", fontSize: "0.9rem" }}>
              <RadioButton
                name="status"
                value="Active"
                checked={formData.status === "Active"}
                onChange={() => onFormChange("status", "Active")}
                text="Active"
              />
              <RadioButton
                name="status"
                value="Inactive"
                checked={formData.status === "Inactive"}
                onChange={() => onFormChange("status", "Inactive")}
                text="Inactive"
              />
            </FlexBox>
          </div>

          {/* A8. Bilang ng miyembro ng pamilya */}
          <div>
            <Label style={{ fontWeight: "bold", fontSize: "1rem" }}>A8. Family Members</Label>
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
            <Input
              name="numFamilyMembers"
              value={formData.numFamilyMembers || ""}
              onInput={(e) => onFormChange("numFamilyMembers", e.target.value)}
              placeholder="Number of Family Members"
              type="Number"
              style={{ width: "100%", fontSize: "0.9rem" }}
              min="1"
              required
            />
          </div>

          {/* A9. Unang Taong nanirahan ang puno ng pamilya sa barangay */}
          <div>
            <Label style={{ fontWeight: "bold", fontSize: "1rem" }}>A9. Year Moved</Label>
            <Input
              name="yearMoved"
              value={formData.yearMoved || ""}
              onInput={(e) => onFormChange("yearMoved", e.target.value)}
              placeholder="Year"
              type="Number"
              style={{ width: "100%", fontSize: "0.9rem" }}
              min="2000"
              max={new Date().getFullYear()} // Sets the max year to the current year
              pattern="\d{4}" // Ensures exactly 4 digits are entered
            />
          </div>

          {/* A10. Lugar na pinanggalingan ng Puno ng Pamilya bago nanirahan sa barangay */}
          {/* <Label style={{ fontWeight: "bold", fontSize: "1rem" }}>A10. Previous Location</Label>
          <Input
            name="prevLocation"
            value={formData.prevLocation || ""}
            onInput={(e) => onFormChange("prevLocation", e.target.value.toUpperCase())}
            placeholder="Municipality, Province"
            style={{ width: "100%", fontSize: "0.9rem" }}
          /> */}
          <div>
            <Label style={{ fontWeight: "bold" }}>A10. Previous Location - Province</Label>
            <Label style={{ fontWeight: "bold" }}></Label>
            <select
              value={selectedPrevProvince || ""}
              onChange={(e) => {
                const provinceId = e.target.value;
                setSelectedPrevProvince(provinceId);
                onFormChange("prevLocation", "");
                setPrevMunicipalities([]);
                if (provinceId) fetchPrevMunicipalities(provinceId);
              }}
              style={{ width: "100%", padding: "0.5rem" }}
            >
              <option value="">Select Province</option>
              {Array.isArray(provinces) && provinces.length > 0 && 
                prevProvinces.map((province) => (
                  <option key={province.province_id} value={province.province_id}>
                    {province.province_name}
                  </option>
              ))}
            </select>
          </div>
          <div>
            <Label style={{ fontWeight: "bold" }}>A10.1. Previous Location - City/Municipality</Label>
            <select
              value={formData.prevLocation || ""}
              onChange={(e) => {
                const municipalityId = e.target.value;
                onFormChange("prevLocation", municipalityId);
              }}
              disabled={!selectedPrevProvince}
              style={{ width: "100%", padding: "0.5rem" }}
            > 
              <option value="">Select Municipality</option>
              {prevMunicipalities.map((municipality) => (
                <option key={municipality.municipality_id} value={municipality.municipality_id}>
                  {municipality.municipality_name}
                </option>
              ))}
            </select>
          </div>
          
          
        </div>
      </div>

      <br />

      {/* <Button design="Emphasized" onClick={handleNext}>Next</Button> */}
      {mapDialogOpen && (
      <Dialog 
        open={mapDialogOpen} 
        headerText="Select Location" 
        onAfterClose={closeMapModal} 
        style={{ width: "80vw", height: "80vh" }}
      >
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <div style={{ padding: "1rem" }}>
            <Input
              id="autocomplete-input"
              placeholder="Search for a place (e.g. Mall of Asia)"
              style={{ width: "100%", marginBottom: "1rem" }}
              required
            />
          </div>
          <div 
            ref={mapRef} 
            style={{ flex: 1, minHeight: "500px", border: "1px solid #ccc" }}
          ></div>
          <div style={{ padding: "1rem", display: "flex", justifyContent: "flex-end" }}>
            <Button design="Emphasized" onClick={closeMapModal}>
              Confirm Location
            </Button>
          </div>
        </div>
      </Dialog>
    )}
    </div>
  );
};

export default ManualForm;